import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatErr } from "@/lib/api";

const COHORTS = [["cosmic", "Cosmic Business Blueprint"], ["apprenticeship", "Home Care Apprenticeship"]];

const MemberRow = ({ m, onSaved }) => {
  const save = async (patch) => {
    try {
      const { data } = await api.patch(`/admin/users/${m.id}`, patch);
      onSaved(data);
      toast.success(`${data.name} has been updated.`);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const status = m.status || "approved";
  const toggle = (k) => save({ enrollments: m.enrollments.includes(k) ? m.enrollments.filter((x) => x !== k) : [...m.enrollments, k] });
  return (
    <tr className="border-t border-[#dacac1]" data-testid={`member-row-${m.id}`}>
      <td className="px-5 py-4"><p className="font-medium">{m.name}</p><p className="text-xs text-[#232b1b]/60">{m.email}</p>{m.phone && <p className="text-xs text-[#232b1b]/60">{m.phone}</p>}
        {m.intake?.heard_from && (
          <details className="mt-2 text-xs max-w-sm" data-testid={`member-intake-${m.id}`}>
            <summary className="cursor-pointer text-[#b6689a]">Their application answers</summary>
            <div className="mt-2 space-y-2 text-[#232b1b]/80">
              <p><strong>Heard about us:</strong> {m.intake.heard_from}</p>
              <p><strong>What interests them:</strong> {m.intake.interest}</p>
              <p><strong>Readiness:</strong> {m.intake.readiness}/10</p>
              <p><strong>Number 1 thing to heal, learn, remember:</strong> {m.intake.focus}</p>
            </div>
          </details>
        )}</td>
      <td className="px-5 py-4" data-testid={`member-status-${m.id}`}>
        {status === "approved" ? (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-[#577731]">Approved</span>
            <Link to={`/members/messages?to=${m.id}`} className="text-xs underline underline-offset-4 text-[#b6689a]" data-testid={`member-message-${m.id}`}>Message</Link>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-xs uppercase tracking-widest ${status === "pending" ? "text-[#b6689a]" : "text-[#232b1b]/50"}`}>{status === "pending" ? "Awaiting you" : "Declined"}</span>
            <button onClick={() => save({ status: "approved" })} className="btn btn-olive !py-1.5 !px-4 text-xs" data-testid={`member-approve-${m.id}`}>Approve</button>
            {status === "pending" && <button onClick={() => save({ status: "declined" })} className="btn btn-ghost !py-1.5 !px-4 text-xs" data-testid={`member-decline-${m.id}`}>Decline</button>}
          </div>
        )}
      </td>
      <td className="px-5 py-4">
        <select value={m.role} onChange={(e) => save({ role: e.target.value })} className="field !py-2 !w-auto" data-testid={`member-role-${m.id}`}>
          <option value="ambassador">Ambassador</option>
          <option value="instructor">Instructor</option>
          <option value="admin">Admin</option>
        </select>
      </td>
      {COHORTS.map(([k]) => (
        <td key={k} className="px-5 py-4">
          <Checkbox checked={m.enrollments.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`member-enroll-${k}-${m.id}`} />
        </td>
      ))}
    </tr>
  );
};

export const AdminMembers = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/users").then((r) => setRows(r.data));
  }, []);
  const onSaved = (u) => setRows((rs) => rs.map((r) => (r.id === u.id ? u : r)));
  const pending = rows.filter((r) => r.status === "pending").length;
  const sorted = [...rows].sort((a, b) => (b.status === "pending") - (a.status === "pending"));
  return (
    <div>
    <p className="mb-4 text-sm text-[#232b1b]/70" data-testid="admin-pending-count">
      {pending ? `${pending} ${pending === 1 ? "person is" : "people are"} waiting for your approval.` : "No one is waiting for approval right now."}
    </p>
    <div className="card-soft overflow-x-auto" data-testid="admin-members">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-widest text-[#232b1b]/60">
          <tr><th className="px-5 py-4">Member</th><th className="px-5 py-4">Membership</th><th className="px-5 py-4">Role</th>{COHORTS.map(([k, l]) => <th key={k} className="px-5 py-4">{l}</th>)}</tr>
        </thead>
        <tbody>{sorted.map((m) => <MemberRow key={m.id} m={m} onSaved={onSaved} />)}</tbody>
      </table>
    </div>
    </div>
  );
};
